import PropTypes from "prop-types";
import React from "react";
import { Icon, Box, Loading, Flex, Small, Avatar } from "orcs-design-system";

import { getTextColorSync } from "src/util/getTextContrastColor";
import { DEFAULT_BACKGROUND } from "src/components/BannerImagePicker/consts";
import {
  MasonryCardContainer,
  MasonryImage,
  MasonryLabel,
  MasonryAuthor,
  DeleteButton,
  MasonryCardButton,
  MasonryBackground,
} from "../GenericMasonry.styled";
import { DEFAULT_CARD_WIDTH } from "../consts";

const PLACEHOLDER_IMAGE =
  "https://blocks.astratic.com/img/general-img-landscape.png";

const MasonryCard = ({
  data,
  onClick,
  isSelected,
  isDeleting,
  cardWidth = DEFAULT_CARD_WIDTH,
  cardLabelHeight = 30,
  onDelete,
  isUploading,
  isLoading,
  avatarMode = false,
}) => {
  const imageRatio = avatarMode ? 1 : data.width / data.height;
  const cardHeight = cardWidth / imageRatio;

  if (!data?.id) {
    return null;
  }

  return (
    <MasonryCardContainer
      id={`cp-masonry-card-${data.id}`}
      width={cardWidth}
      height={cardHeight}
      color={data.color}
      isSelected={isSelected}
      isDeleting={isDeleting}
      isUploading={isUploading}
      isLoading={isLoading}
      isSelectable={!!onClick}
      avatarMode={avatarMode}
    >
      {onDelete && (
        <Box position="absolute" top="0" right="0" zIndex="1000" p="s">
          <DeleteButton
            small
            iconOnly
            p="s"
            onClick={onDelete}
            disabled={isDeleting}
          >
            <Icon icon={["far", "trash-alt"]} />
          </DeleteButton>
        </Box>
      )}
      {isUploading && (
        <Flex
          position="absolute"
          height="100%"
          width="100%"
          p="s"
          alignItems="flex-end"
          justifyContent="flex-start"
          zIndex="5"
        >
          <Loading inverted />
        </Flex>
      )}
      <MasonryCardButton
        type="button"
        aria-pressed={isSelected}
        data-testid={`cp-masonry-card-${data.id}`}
        onClick={onClick}
      >
        {data?.type === DEFAULT_BACKGROUND ? (
          avatarMode ? (
            <Avatar
              headerAvatar
              sizing="large"
              shape="square"
              gradient={data.color}
            />
          ) : (
            <MasonryBackground
              color={data.color}
              style={{ position: "relative" }}
            >
              <Box
                position="absolute"
                bottom={0}
                right={3}
                p="xs"
                textAlign="right"
                maxWidth="100%"
              >
                <Small fontSize="1.1rem" color={getTextColorSync(data.color)}>
                  {data.description}
                </Small>
              </Box>
            </MasonryBackground>
          )
        ) : avatarMode ? (
          <Avatar
            headerAvatar
            sizing="large"
            shape="square"
            image={data.src || PLACEHOLDER_IMAGE}
            imageAlt={data.alt}
          />
        ) : (
          <MasonryImage
            data-testid={`cp-masonry-img-${data.id}`}
            src={data.src || PLACEHOLDER_IMAGE}
            alt={data.alt}
          />
        )}
        {data.author && !avatarMode && (
          <MasonryLabel py={2} height={cardLabelHeight}>
            Photo by:&nbsp;
            {data.authorLink ? (
              <MasonryAuthor
                href={data.authorLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {data.author}
              </MasonryAuthor>
            ) : (
              data.author
            )}
          </MasonryLabel>
        )}
      </MasonryCardButton>
    </MasonryCardContainer>
  );
};

MasonryCard.propTypes = {
  isSelected: PropTypes.bool,
  data: PropTypes.object,
  onClick: PropTypes.func,
  cardWidth: PropTypes.number,
  cardLabelHeight: PropTypes.number,
  onDelete: PropTypes.func,
  isDeleting: PropTypes.bool,
  isUploading: PropTypes.bool,
  isLoading: PropTypes.bool,
  avatarMode: PropTypes.bool,
};

export default MasonryCard;
